/**
 * Clone a Kuutio or a dataset: onto this server under a new name, or out as a clone
 * package ("Vie Kuutio" / "Vie tietoaineisto") to import in another environment.
 * Both use the same choice of content. specs/clone-packages.md
 */
import React, { useState } from 'react';
import { Copy, Download } from 'lucide-react';
import { Button, Actions, StatusLine, FieldHelp, Label } from '../InstanceSettings/formPrimitives';
import { useTranslation } from '../../hooks/useTranslation.jsx';
import CloneNameField from './CloneNameField';
import CloneResult from './CloneResult';
import { cloneOnServer, downloadPackage } from './cloneApi';

function ModeChoice({ mode, onChange }) {
  const { t } = useTranslation();
  return (
    <div role="radiogroup" aria-label={t('clone.modeLabel')}>
      <Label>{t('clone.modeLabel')}</Label>
      {['prepared', 'everything'].map((m) => (
        <label key={m} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', margin: '6px 0', cursor: 'pointer' }}>
          <input type="radio" name="clone-mode" value={m} checked={mode === m} onChange={() => onChange(m)} />
          <span>
            <strong>{t(m === 'prepared' ? 'clone.modePrepared' : 'clone.modeEverything')}</strong>
            <FieldHelp style={{ margin: 0 }}>{t(m === 'prepared' ? 'clone.modePreparedHelp' : 'clone.modeEverythingHelp')}</FieldHelp>
          </span>
        </label>
      ))}
      <FieldHelp>{t('clone.neverCopied')}</FieldHelp>
    </div>
  );
}

function FilesChoice({ includeFiles, onChange }) {
  const { t } = useTranslation();
  return (
    <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', cursor: 'pointer' }}>
      <input type="checkbox" checked={includeFiles} onChange={(e) => onChange(e.target.checked)} />
      <span>
        {t('clone.includeFiles')}
        <FieldHelp style={{ margin: 0 }}>{t('clone.includeFilesHelp')}</FieldHelp>
      </span>
    </label>
  );
}

export default function ClonePanel({ kind, sourceId, sourceName, onOpenClone }) {
  const { t } = useTranslation();
  const [mode, setMode] = useState('prepared');
  const [includeFiles, setIncludeFiles] = useState(true);
  const [name, setName] = useState('');
  const [available, setAvailable] = useState(false);
  const [busy, setBusy] = useState(null); // 'clone' | 'export'
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const options = { mode, includeFiles };

  const runClone = async () => {
    setBusy('clone');
    setError(null);
    setResult(null);
    try {
      setResult(await cloneOnServer(kind, sourceId, { name, ...options }));
      setName('');
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(null);
    }
  };

  const runExport = async () => {
    setBusy('export');
    setError(null);
    try { await downloadPackage(kind, sourceId, options); } catch (err) { setError(err.message); } finally { setBusy(null); }
  };

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      {kind === 'instance'
        ? <ModeChoice mode={mode} onChange={setMode} />
        : <FilesChoice includeFiles={includeFiles} onChange={setIncludeFiles} />}

      <div>
        <Label as="div" style={{ fontWeight: 600 }}>{t('clone.sameServerTitle')}</Label>
        <CloneNameField id={`clone-name-${kind}`} kind={kind} value={name} onChange={setName} onAvailable={setAvailable}
          placeholder={t('clone.namePlaceholder', { name: sourceName || '' })} />
        <Actions>
          <Button type="button" $variant="primary" onClick={runClone} disabled={!available || !!busy}>
            <Copy /> {busy === 'clone' ? t('clone.cloning') : t('clone.cloneButton')}
          </Button>
        </Actions>
        {result && <CloneResult result={result} onOpen={onOpenClone} />}
      </div>

      <div>
        <Actions>
          <Button type="button" onClick={runExport} disabled={!!busy}>
            <Download /> {busy === 'export' ? t('clone.exporting') : t(kind === 'dataset' ? 'clone.exportDataset' : 'clone.exportInstance')}
          </Button>
        </Actions>
        <FieldHelp>{t(kind === 'dataset' ? 'clone.exportHelpDataset' : 'clone.exportHelpInstance')}</FieldHelp>
      </div>

      {error && <StatusLine $error>{t('clone.failed', { error })}</StatusLine>}
    </div>
  );
}
